import React from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Building2, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { getBorderColor, getBorderWidth, getButtonColor, isBranchClosed } from '../utils/orderPopupUtils'
import home1Icon from '../../../assets/images/home1.svg'

interface Branch {
  id: number
  name: string
  descriptiveAddress?: string
  is_available?: boolean
  isClosed?: boolean
  status?: string
}

interface BranchComponentsProps {
  isLoadingBranches: boolean
  filteredBranches: Branch[]
  branches: Branch[]
  selectedBranch: Branch | null
  searchQuery: string
  onSearchChanged: (query: string) => void
  onBranchSelected: (branch: Branch) => void
  onDone: () => void
  isRTL: boolean
}

/**
 * Branch selection content component (for branch step)
 */
export const BranchSelectionContent: React.FC<BranchComponentsProps> = ({
  isLoadingBranches,
  filteredBranches,
  branches,
  selectedBranch,
  searchQuery,
  onSearchChanged,
  onBranchSelected,
  onDone,
  isRTL,
}) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar */}
      <div className="px-4 pt-2 pb-1 ">
          <div className="relative  border border-gray-200 rounded-xl">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChanged(e.target.value)}
              placeholder={t('cs.search_branches') || 'ابحث عن الفروع'}
              className={`
                w-full h-12 px-10 rounded-xl 
                text-sm font-medium font-['IBM_Plex_Sans_Arabic']
bg-white                border-none ring-0 focus:ring-0 focus:ring-offset-0 focus:border-none
                ${isRTL ? 'text-right' : 'text-left'}
              `}
            />
          </div>
      </div>

      {/* Branches List */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 pb-[90px]">
        {isLoadingBranches ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]"></div>
          </div>
        ) : filteredBranches.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-gray-600 font-['IBM_Plex_Sans_Arabic']">
              {branches.length === 0
                ? t('cs.no_branches_available') || 'لا توجد فروع متاحة'
                : t('cs.no_search_results') || 'لا توجد نتائج للبحث'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredBranches.map((branch) => {
              const isSelected = selectedBranch?.id === branch.id
              const isClosed = isBranchClosed(branch)

              return (
                <button
                  key={branch.id}
                  onClick={() => !isClosed && onBranchSelected(branch)}
                  disabled={isClosed}
                  className={`
                    w-full min-h-[60px] px-4 py-4 rounded-xl flex items-center justify-between gap-3
                    transition-all
                    ${isClosed ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-300'}
                  `}
                  style={{
                    border: `${getBorderWidth(isSelected)}px solid ${getBorderColor(isSelected)}`,
                    backgroundColor: isSelected ? 'transparent' : '#FFFFFF',
                  }}
                >
                  <div className={`flex items-center gap-2 flex-1 `}>
                    <img
                      src={home1Icon}
                      alt="branch"
                      className="w-9 h-10 flex-shrink-0"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                    <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <p className="text-[15px] font-bold text-gray-900 font-['IBM_Plex_Sans_Arabic'] mb-1">
                        {branch.name || t('cs.branch_undefined') || 'فرع غير محدد'}
                      </p>
                      {branch.descriptiveAddress && (
                        <p className="text-[13px] font-normal text-gray-600 font-['IBM_Plex_Sans_Arabic'] line-clamp-2">
                          {branch.descriptiveAddress}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Radio Button */}
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      border: `2px solid ${isSelected ? 'var(--brand-primary)' : '#9CA3AF'}`,
                      backgroundColor: isSelected ? '#FFFFFF' : 'transparent',
                    }}
                  >
                    {isSelected && (
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: 'var(--brand-primary)' }}
                      />
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Done Button - Fixed at bottom */}
      <div className="px-4 pb-4 pt-2 sticky bottom-0 z-10 bg-white/95 backdrop-blur-sm">
        <button
          onClick={onDone}
          disabled={!selectedBranch || isBranchClosed(selectedBranch)}
          className={`
            w-full h-[65px] rounded-xl
            flex items-center justify-center
            font-semibold text-base font-['IBM_Plex_Sans_Arabic']
            transition-all
            ${selectedBranch && !isBranchClosed(selectedBranch)
              ? 'hover:opacity-90'
              : 'cursor-not-allowed opacity-50'
            }
          `}
          style={{
            backgroundColor: selectedBranch && !isBranchClosed(selectedBranch)
              ? getButtonColor(true)
              : getButtonColor(false),
            color: selectedBranch && !isBranchClosed(selectedBranch) ? '#FFFFFF' : '#9CA3AF',
          }}
        >
          {t('common.done') || 'تم'}
        </button>
      </div>
    </div>
  )
}

/**
 * Build branch selection content - wrapper for backward compatibility
 */
export const buildBranchSelectionContent = (props: BranchComponentsProps): React.ReactElement => {
  return <BranchSelectionContent {...props} />
}

/**
 * Branch selection section component (for main step)
 */
export const BranchSelectionSection: React.FC<{
  selectedBranch: Branch | null
  onTap: () => void
  onClear?: () => void
  isRTL: boolean
}> = ({
  selectedBranch,
  onTap,
  onClear,
  isRTL,
}) => {
  const { t } = useTranslation()

  const hasSelectedBranch = selectedBranch !== null

  return (
    <div className="w-full">
      <h3 className="text-base font-semibold text-gray-700 mb-3 font-['IBM_Plex_Sans_Arabic']">
        {t('cs.select_branch') || 'اختر الفرع'} <span className="text-red-500">*</span>
      </h3>

      <button
        onClick={onTap}
        className={`
          w-full h-14 px-4 rounded-xl flex items-center justify-between gap-3
          border border-gray-200 bg-white  transition-all
          flex-row-reverse
        `}
      >
          <div className="flex items-center gap-2">
          {hasSelectedBranch && onClear && (
            <div
              onClick={(e) => {
                e.stopPropagation()
                onClear()
              }}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onClear()
                }
              }}
            >
              <X className="w-4 h-4 text-gray-600" />
            </div>
          )}
          {!hasSelectedBranch && (
            isRTL ? (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )
          )}
        </div>
        <div className={`flex items-center`}>
          <span className={`text-sm font-medium font-['IBM_Plex_Sans_Arabic'] fle`}>
            {selectedBranch && typeof selectedBranch === 'object'
              ? selectedBranch.name || t('cs.selected_branch') || 'تم اختيار الفرع'
              : t('cs.choose_branch_placeholder') || 'اختر الفرع'}
          </span>
        </div>

        
      </button>
    </div>
  )
}

/**
 * Build branch selection section - wrapper for backward compatibility
 */
export const buildBranchSelectionSection = (props: {
  selectedBranch: Branch | null
  onTap: () => void
  onClear?: () => void
  isRTL: boolean
}): React.ReactElement => {
  return <BranchSelectionSection {...props} />
}

